import { AssetIdSchema, ChunkIdSchema, ConceptIdSchema, SeedIdSchema, ConceptSchema } from "@storytell/types";
import z from "zod";

export const DistanceSchema = z.record(z.string(), z.number());

// ChunkWithDistances exists to support showing a "graph" of a seed in the
// Storytell UI. Every chunk will have the distance from the seed and then the
// distance to every other chunk that is a member of the seed.
export const ChunkWithDistancesSchema = z.object({
  id: ChunkIdSchema,
  text: z.string(),
  assetId: AssetIdSchema,
  modifiedAt: z.string(),
  distanceFromSeed: z.number(),
  distances: DistanceSchema.optional(),
});
export type ChunkWithDistances = z.infer<typeof ChunkWithDistancesSchema>;

// SeedWithDistances to provide a graph of "seeds" for a concept. The distance
// defined in DistanceFromConcept communicates how far the seed is from the
// concept (larger = further) and the Distances field contains a map showing the
// distance to every other seed that belongs to a Concept.
export const SeedWithDistancesSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  summary: z.string(),
  chunks: z.array(ChunkWithDistancesSchema).optional(),
  distanceFromConcept: z.number(),
  distances: DistanceSchema.optional(),
  createdAt: z.iso.datetime(),
  createdBy: z.string(),
});
export type SeedWithDistances = z.infer<typeof SeedWithDistancesSchema>;

export const ConceptWithDistancesSchema = z.object({
  id: z.string(),
  boundary: z.string(),
  name: z.string(),
  description: z.string(),
  summary: z.string(),
  seeds: z.array(SeedWithDistancesSchema).optional(),
  distances: DistanceSchema.optional(),
  createdAt: z.iso.datetime(),
  modifiedAt: z.iso.datetime(),
});
export type ConceptWithDistances = z.infer<typeof ConceptWithDistancesSchema>;

export const ConceptsWithDistancesSchema = z.object({
  concepts: z.array(ConceptWithDistancesSchema),
});
export type ConceptsWithDistances = z.infer<typeof ConceptsWithDistancesSchema>;

export const ConceptChunkSchema = z.object({
  id: ChunkIdSchema,
  text: z.string(),
});
export type ConceptChunk = z.infer<typeof ConceptChunkSchema>;

export const ConceptChunksSchema = z.object({
  conceptId: ConceptIdSchema,
  seeds: z.record(
    z.string(),
    z.object({
      assetId: AssetIdSchema,
      seedId: z.string(),
      chunks: z.record(ChunkIdSchema, ConceptChunkSchema),
    }),
  ),
  seedIDs: z.array(SeedIdSchema).optional().nullable(),
  chunkIDs: z.array(ChunkIdSchema).optional().nullable(),
});
export type ConceptChunks = z.infer<typeof ConceptChunksSchema>;

// Sync endpoint returns a map of concept IDs to concept objects
export const ConceptsSyncSchema = z.object({
  concepts: z.record(ConceptIdSchema, ConceptSchema),
  conceptSlider: z.any().optional(),
});
export type ConceptsSync = z.infer<typeof ConceptsSyncSchema>;
